<template>
  <v-dialog :value="value" max-width="520" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-h6 font-weight-bold">Add a version</v-card-title>
      <v-card-subtitle v-if="document" class="pb-2">
        {{ document.documentNumber }}<span v-if="document.documentTitle && document.documentTitle != document.documentNumber"> · {{ document.documentTitle }}</span>
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text class="pt-4">
        <v-form ref="form" @submit.prevent="submit">
          <v-file-input
            v-model="file"
            label="File"
            outlined
            dense
            show-size
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            :rules="[(v) => !!v || 'Choose a file', (v) => !v || v.size > 0 || 'The file is empty']"></v-file-input>
          <div v-if="comparing" class="d-flex align-center text-caption text--secondary mt-n3 mb-3" style="gap: 6px">
            <v-progress-circular indeterminate size="12" width="2" />
            Comparing with the current version…
          </div>
          <IdenticalFileNotice
            v-else-if="identicalVersion"
            class="mt-n2 mb-4"
            :version-number="identicalVersion.versionNumber"
            :file-name="identicalVersion.fileName"
            :size="Number(identicalVersion.size)"
            :content-hash="identicalVersion.contentHash"
            hint="Choose a different file, or upload it anyway to keep a copy as its own version." />
          <v-text-field
            v-model="revisionNumber"
            label="Revision"
            outlined
            dense
            hint="The engineering revision label, for example B or 02."
            persistent-hint
            :rules="[(v) => !v || v.length <= 50 || 'At most 50 characters']"></v-text-field>

          <div v-if="linksLoading" class="d-flex align-center text-body-2 mt-4" style="gap: 8px">
            <v-progress-circular indeterminate size="16" width="2" color="primary" />
            Checking the links on this document…
          </div>
          <div v-else-if="linkCount > 0" class="adv__links-prompt mt-4">
            <div class="text-body-2">
              <strong>{{ linkCountLabel(linkCount) }}</strong> placed on
              {{ linkVersionNumber ? `v${linkVersionNumber}` : 'the current version' }}.
              Links belong to one version. Transfer them to the new version?
            </div>
            <v-radio-group
              v-model="transferLinks"
              dense
              class="mt-2"
              :rules="[(v) => v !== null || 'Choose whether to transfer the links']">
              <v-radio :value="true" label="Transfer the links to the new version"></v-radio>
              <v-radio :value="false" label="Start the new version without links"></v-radio>
            </v-radio-group>
          </div>
        </v-form>
        <div v-if="nextVersionNumber" class="text-body-2 mt-4">
          Will be saved as <strong>v{{ nextVersionNumber }}</strong>.
        </div>
        <v-alert
          v-if="document && document.isCurrentOverridden"
          dense
          outlined
          type="warning"
          class="mt-3 mb-0"
          style="font-size: 0.85rem">
          v{{ document.currentVersionNumber }} is pinned as current. The new version will not be shown by default
          until you revert to the latest version.
        </v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn text @click="close">Cancel</v-btn>
        <v-spacer></v-spacer>
        <span class="text-caption mr-3 adv__note">Uploads run in the background</span>
        <v-btn color="primary" depressed :disabled="linksLoading" :loading="checking" @click="submit">
          <v-icon small left>mdi-tray-arrow-up</v-icon>
          {{ identicalVersion && !comparing ? 'Upload anyway' : 'Upload' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import documentVersionsService from '../service/documentVersions.service';
  import { getDocumentVersions } from '../utils/viewerVersionNotice';
  import { findIdenticalCurrentVersion, linkCountLabel } from '../utils/documentVersions';
  import { error as debugError } from '@/utils/debugLogger';
  import IdenticalFileNotice from './IdenticalFileNotice.vue';

  /**
   * "Add a version" of one document: file, revision label and, when the current version has links,
   * whether to transfer them. A chosen file identical to the current version is flagged inline; the
   * user picks another file or uploads it anyway. Hands the file to the background upload queue
   * (DocumentUploadTray) and closes; DOCUMENT_CHANGED tells open views to reload.
   */
  export default {
    name: 'AddDocumentVersionDialog',
    components: { IdenticalFileNotice },
    props: {
      value: { type: Boolean, default: false },
      documentId: { type: [String, Number], required: true },
      /** DocumentVM-like: documentNumber, documentTitle, fileName, revisionNumber, isCurrentOverridden, currentVersionNumber */
      document: { type: Object, default: null },
      /** DocumentVersionVMs already loaded by the parent; the duplicate check loads them when the current one is missing. */
      versions: { type: Array, default: () => [] },
      nextVersionNumber: { type: Number, default: null },
    },
    data() {
      return {
        file: null,
        revisionNumber: '',
        // Links on the version that is current before the upload; null transferLinks = not answered.
        linksLoading: false,
        linkCount: 0,
        linkVersionNumber: null,
        transferLinks: null,
        checking: false,
        // The current version when the chosen file has exactly its content.
        identicalVersion: null,
        comparing: false,
        comparison: null,
      };
    },
    watch: {
      value: {
        immediate: true,
        handler(open) {
          if (open) this.reset();
        },
      },
      file() {
        this.compareWithCurrent();
      },
    },
    methods: {
      linkCountLabel,
      reset() {
        this.file = null;
        // The revision label usually carries over; the user changes it when it moves on.
        this.revisionNumber = this.document?.revisionNumber || '';
        this.linkCount = 0;
        this.linkVersionNumber = null;
        this.transferLinks = null;
        this.checking = false;
        this.identicalVersion = null;
        this.$nextTick(() => this.$refs.form?.resetValidation());
        this.loadLinkCount();
      },
      close() {
        this.$emit('input', false);
      },
      /** Links on the current version, so the dialog can ask whether to transfer them. */
      async loadLinkCount() {
        const documentId = this.documentId;
        this.linksLoading = true;
        try {
          const result = await documentVersionsService.getLinkCount(documentId);
          if (documentId !== this.documentId || !this.value) return;
          this.linkCount = result?.linkCount || 0;
          this.linkVersionNumber = result?.versionNumber ?? null;
          this.transferLinks = this.linkCount > 0 ? null : false;
        } catch (err) {
          // Without the count there is nothing to ask; the version is added without links.
          debugError('[AddDocumentVersionDialog]', 'Could not count the links:', err);
          this.linkCount = 0;
          this.transferLinks = false;
        } finally {
          this.linksLoading = false;
        }
      },
      /** The current version when the chosen file has exactly its content. A failed check never blocks the upload. */
      async identicalCurrentVersion() {
        try {
          let versions = this.versions;
          if (!versions.some((v) => v.isCurrent)) versions = await getDocumentVersions(this.documentId);
          return await findIdenticalCurrentVersion(this.file, versions);
        } catch (err) {
          debugError('[AddDocumentVersionDialog]', 'Could not compare with the current version:', err);
          return null;
        }
      },
      /** Runs the check for the chosen file; a newer choice makes an older result irrelevant. */
      compareWithCurrent() {
        const file = this.file;
        this.identicalVersion = null;
        if (!file) {
          this.comparing = false;
          this.comparison = null;
          return null;
        }
        this.comparing = true;
        const comparison = this.identicalCurrentVersion().then((identical) => {
          if (this.comparison !== comparison) return;
          this.identicalVersion = identical;
          this.comparing = false;
        });
        this.comparison = comparison;
        return comparison;
      },
      async submit() {
        if (this.linksLoading || this.checking || !this.$refs.form?.validate()) return;

        // "Upload anyway" was on the button: the user saw the notice and keeps the copy.
        const keepsCopy = !!this.identicalVersion && !this.comparing;
        if (this.comparing) {
          this.checking = true;
          await this.comparison;
          this.checking = false;
        }
        // The check found a copy after the click: show the notice and let the user decide.
        if (this.identicalVersion && !keepsCopy) return;
        const allowDuplicateContent = keepsCopy;

        const doc = this.document;
        this.$store.dispatch('DOCUMENT_UPLOADS/ENQUEUE_VERSION', {
          documentId: this.documentId,
          file: this.file,
          revisionNumber: this.revisionNumber,
          transferLinks: this.transferLinks === true,
          allowDuplicateContent,
          documentLabel: doc?.documentNumber || doc?.documentTitle || doc?.fileName || '',
        });
        this.close();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .adv__note {
    opacity: 0.6;
  }

  .adv__links-prompt {
    padding: 12px 12px 0;
    border: 1px solid var(--ds-border-default);
    border-radius: var(--ds-radius-md, 8px);
  }
</style>
